import { useCallback, useRef, useState } from 'react';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { SocialAuthResult } from '@wlb/shared/types';
import { socialAuthToken, socialProviderName, type SocialProvider } from './socialProviders';

interface UseSocialAuthOptions {
  /** `login` exchanges via LoginSSO, `signup` via CreateAccountSSO. */
  mode: 'login' | 'signup';
  /** Runs only after the SSO exchange resolves successfully. */
  onSuccess?: () => void;
}

interface UseSocialAuthValue {
  pending: SocialProvider | null;
  error: string;
  /** Drives providers we invoke ourselves (Facebook, Apple). */
  start: (provider: SocialProvider) => Promise<void>;
  /** Consumes a result produced by a provider's own UI (Google's button). */
  complete: (provider: SocialProvider, result: SocialAuthResult) => Promise<void>;
}

/**
 * Shared social sign-in flow for the Login and SignUp screens, mirroring the
 * mobile auth screens: provider SDK → token → SSO exchange → onSuccess.
 */
export function useSocialAuth({ mode, onSuccess }: UseSocialAuthOptions): UseSocialAuthValue {
  const {
    signInWithFacebook,
    signInWithApple,
    loginSSO,
    signUpSSO,
    error: authError,
    clearError,
  } = useAuth();
  const { t } = useTranslation();
  const [pending, setPending] = useState<SocialProvider | null>(null);
  const [localError, setLocalError] = useState('');
  const attemptedRef = useRef(false);

  const complete = useCallback(
    async (provider: SocialProvider, result: SocialAuthResult): Promise<void> => {
      setLocalError('');
      clearError();

      if (!result?.success) {
        // A user-initiated cancel is not a failure worth surfacing.
        if (!result?.cancelled) setLocalError(t('auth.social.error'));
        setPending(null);
        return;
      }

      const token = socialAuthToken(provider, result);
      if (!token) {
        setLocalError(t('auth.social.error'));
        setPending(null);
        return;
      }

      setPending(provider);
      attemptedRef.current = true;
      try {
        const name = socialProviderName(provider);
        const rawName = result.user?.name;
        const firstName = rawName?.trim().split(' ')[0] || undefined;
        if (mode === 'signup') {
          await signUpSSO(name, token, result.user?.email, undefined, firstName);
        } else {
          await loginSSO(name, token, result.user?.email);
        }
        onSuccess?.();
      } catch {
        // Both SSO calls already wrote state.error and raised a toast.
      } finally {
        setPending(null);
      }
    },
    [mode, clearError, loginSSO, signUpSSO, onSuccess, t],
  );

  const start = useCallback(
    async (provider: SocialProvider): Promise<void> => {
      if (pending) return;
      setPending(provider);
      setLocalError('');
      clearError();

      try {
        const result =
          provider === 'facebook' ? await signInWithFacebook() : await signInWithApple();
        await complete(provider, result);
      } catch {
        setLocalError(t('auth.social.error'));
        setPending(null);
      }
    },
    [pending, clearError, signInWithFacebook, signInWithApple, complete, t],
  );

  return {
    pending,
    error: localError || (attemptedRef.current ? (authError ?? '') : ''),
    start,
    complete,
  };
}
